import { Application, ApplicationOptions, CullerPlugin, extensions } from 'pixi.js';
import { IViewportOptions } from 'pixi-viewport';

import { AssetManager } from '../assets/AssetManager';
import { AudioManager } from '../audio/AudioManager';
import { CameraManager } from '../camera/CameraManager';
import { CursorManager } from '../input';
import { KeyboardInputHandler } from '../input/KeyboardInputHandler';
import { DefaultLoadingScene } from '../scene/DefaultLoadingScene';
import { SceneManager } from '../scene/SceneManager';

import { IGameOptions } from './IGameOptions';

const DEFAULT_GAME_SETTINGS: Partial<ApplicationOptions> = {

  // Rendering options
  canvas: document.getElementById('pixi-canvas') as HTMLCanvasElement,
  antialias: true,
  resolution: window.devicePixelRatio || 1,
  backgroundAlpha: 0,
  clearBeforeRender: true,
  premultipliedAlpha: true,
  preserveDrawingBuffer: false,

  // Performance options
  autoStart: true,
  sharedTicker: true,
  culler: { updateTransform: true },

  // Automatic resize options
  resizeTo: document.getElementById('pixi-canvas') as HTMLCanvasElement,
  autoDensity: true,

  // Other options
  preference: ['webgl', 'canvas'],
  powerPreference: 'high-performance',
  hello: true
};

/**
 * The main entry loop for the game.
 * Initializes the Pixi application and all framework systems.
 */
export abstract class GameManager {
  public static app: Application;

  public static debugEnabled = false;

  private static _resizeTimeoutId: number | null = null;

  public static get screenWidth(): number {
    return window.innerWidth;
  }

  public static get screenHeight(): number {
    return window.innerHeight;
  }

  public static get screenSize(): { width: number; height: number } {
    return { width: GameManager.screenWidth, height: GameManager.screenHeight };
  }

  public static get worldWidth(): number {
    return GameManager.app.renderer.width;
  }

  public static get worldHeight(): number {
    return GameManager.app.renderer.height;
  }

  public static async initialize(options: IGameOptions): Promise<void> {
    GameManager.app = new Application();

    await GameManager.app.init({
      ...DEFAULT_GAME_SETTINGS,
      ...options
    });

    extensions.add(CullerPlugin);

    // Allows use of pixi debug extension
    if (options.debug) {
      /* eslint-disable */
      
      GameManager.debugEnabled = true;
      (window as any).__PIXI_DEVTOOLS__ = {
        app: GameManager.app
      };
      
      /* eslint-enable */
    }

    await AssetManager.initialize(options.assetOptions);
    await SceneManager.initialize(DefaultLoadingScene);
    AudioManager.initialize(options.audioStorageId);
    CameraManager.initialize(options.cameraOptions as Partial<IViewportOptions>);
    KeyboardInputHandler.initialize(options.defaultKeybindings);
    await CursorManager.preloadCursors();

    window.addEventListener('resize', this.resize);
    this.resize();
  }

  public static resize = (): void => {
    // Clear any pending resize timeout
    if (this._resizeTimeoutId !== null) {
      clearTimeout(this._resizeTimeoutId);
      this._resizeTimeoutId = null;
    }

    this._resizeTimeoutId = window.setTimeout(() => {
      if (GameManager.app) {
        GameManager.app.renderer.resize(GameManager.screenWidth, GameManager.screenHeight);

        CameraManager.resize();
        SceneManager.resize();
      }
      this._resizeTimeoutId = null;
    }, 250);
  };

  public static destroy(): void {
    // Clear any pending resize timeout
    if (this._resizeTimeoutId !== null) {
      clearTimeout(this._resizeTimeoutId);
      this._resizeTimeoutId = null;
    }

    window.removeEventListener('resize', this.resize);

    // Destroy all managers in reverse order of initialization
    if (SceneManager) {
      SceneManager.destroy();
    }

    if (CameraManager) {
      CameraManager.destroy();
    }

    if (AudioManager) {
      AudioManager.destroy();
    }

    KeyboardInputHandler.destroy();

    // Clean up debug state
    if (GameManager.debugEnabled && (globalThis as any).__PIXI_APP__) {
      delete (globalThis as any).__PIXI_APP__;
    }
    GameManager.debugEnabled = false;

    // Destroy the PIXI application
    if (GameManager.app) {
      GameManager.app.destroy(true, true);
      GameManager.app = null as never;
    }
  }
}
